import React, { useState, useEffect } from 'react';
import { PCRowProps, RowState, ActionType } from './types';
import { formatDateTime, DateTimeDisplay, formatSecondsToHMS } from './utils';
import { InlineAddHandlingTier } from './InlineAddHandlingTier';
import { getDisplayName } from '../../utils/displayUtils';

/**
 * A component that renders a Production Capability row in the hierarchical table.
 * This is the parent row that can be expanded to show handling tiers.
 */
export const ProductionCapabilityRow: React.FC<PCRowProps> = ({
    pc,
    isExpanded,
    onToggleExpand,
    pendingChanges,
    onValueChange,
    onDelete,
    onAddHandlingTier,
    availableTiers = [],
    userCanEdit,
    warehouseId,
    isPSSD
}) => {
    // Apply Default_Network_PC editing restrictions (same as R2F implementation)
    const isDefaultNetworkPC = pc.name === 'Default_Network_PC';
    const canEditDefaultNetworkPC = !isDefaultNetworkPC || (warehouseId === 'DEFAULT');
    const effectiveUserCanEdit = userCanEdit && canEditDefaultNetworkPC;

    // Validation state
    const [selectedAction, setSelectedAction] = useState<string>('NoAction');
    
    // Determine action state (accepted recommendation, pending deletion or pending override)
    const getActionState = (): RowState => {
        const defaultTier = pc.defaultHandlingTier;
        const pendingChange = pendingChanges.get(defaultTier.id);
        
        if (pendingChange) {
            if (pendingChange.action === ActionType.RECOMMENDED) {
                return RowState.RECOMMENDATION_ACCEPTED;
            } else if (pendingChange.action === ActionType.DELETE && !isPSSD) {
                return RowState.DELETE_PENDING;
            } else if (pendingChange.action !== ActionType.NO_ACTION && pendingChange.action !== ActionType.DELETE) {
                return RowState.OVERRIDE_PENDING;
            }
        }
        return RowState.UNCHANGED;
    };
    
    const actionState = getActionState();
    const rowClassName = `production-capability-row ${actionState}`;
    
    // Handle delete - toggle delete status for the PC
    const handleDelete = () => {
        const defaultTier = pc.defaultHandlingTier;
        const pendingChange = pendingChanges.get(defaultTier.id);
        
        // If it's already marked for deletion, cancel it by setting to NO_ACTION
        if (pendingChange?.action === ActionType.DELETE) {
            // Cancel deletion for the default tier
            onValueChange(pc.id, defaultTier.id, 0, ActionType.NO_ACTION);
            
            // Also cancel deletion for any additional tiers if they were marked for deletion
            if (!isPSSD) {
                pc.additionalHandlingTiers.forEach(tier => {
                    const tierPendingChange = pendingChanges.get(tier.id);
                    if (tierPendingChange?.action === ActionType.DELETE) {
                        onValueChange(pc.id, tier.id, 0, ActionType.NO_ACTION);
                    }
                });
            }
        } else {
            // Mark default tier for deletion
            onValueChange(pc.id, defaultTier.id, 0, ActionType.DELETE);
            
            // Also mark all additional tiers for deletion (non-PSSD only)
            if (!isPSSD) {
                pc.additionalHandlingTiers.forEach(tier => {
                    onValueChange(pc.id, tier.id, 0, ActionType.DELETE);
                });
            }
        }
        
        // If onDelete is provided (for API delete), we'll trigger it during save
        // This is just for marking the intention to delete
        if (onDelete) {
            onDelete(pc.id);
        }
    };
    
    // Update states when pendingChanges changes
    useEffect(() => {
        const pendingChange = pendingChanges.get(pc.defaultHandlingTier.id);
        if (pendingChange) {
            setSelectedAction(pendingChange.action);
        } else {
            setSelectedAction('NoAction');
        }
    }, [pendingChanges, pc.defaultHandlingTier.id]);
    
    return (
        <tr className={rowClassName}>
            <td className="expand-cell">
                <button 
                    className="expand-button"
                    onClick={() => onToggleExpand(pc.id)}
                    aria-label={isExpanded ? 'Collapse' : 'Expand'}
                >
                    {isExpanded ? '▼' : '▶'}
                </button>
            </td>
            <td className="pc-name" colSpan={2}>{getDisplayName(pc.name)}</td>
            <td className="current-gsla">
                {/* Parent row doesn't show GSLA values directly anymore */}
                {/* Values are shown in child tier rows */}
            </td>
            <td className="published-date">
                {/* No date display in parent row */}
            </td>
            <td className="recommended-gsla">
                {/* No recommended GSLA in parent row */}
            </td>
            <td className="recommended-date">
                {/* No recommendation date in parent row */}
            </td>
            {userCanEdit && (
                <>
                    <td className="override-cell">
                        {/* No direct override in parent row */}
                    </td>
                    <td className="action-cell">
                        <div className="action-container">
                            {/* Add Handling Tier button - only show when expanded and user can edit - restricted for Default_Network_PC on non-DEFAULT warehouses */}
                            {isExpanded && onAddHandlingTier && availableTiers.length > 0 && (
                                <InlineAddHandlingTier
                                    pcId={pc.id}
                                    availableTiers={availableTiers}
                                    onAdd={onAddHandlingTier}
                                    userCanEdit={effectiveUserCanEdit}
                                />
                            )}
                            
                            {onDelete && effectiveUserCanEdit && (
                                <button
                                    className="delete-tier-button"
                                    onClick={handleDelete}
                                    aria-label={`${pendingChanges.get(pc.defaultHandlingTier.id)?.action === ActionType.DELETE ? 'Cancel delete' : 'Delete'} ${pc.name}`}
                                    title={pendingChanges.get(pc.defaultHandlingTier.id)?.action === ActionType.DELETE ? "Cancel deletion" : "Delete this production capability"}
                                >
                                    {pendingChanges.get(pc.defaultHandlingTier.id)?.action === ActionType.DELETE ? '↩️' : '🗑️'}
                                </button>
                            )}
                        </div>
                    </td>
                </>
            )}
            <td className="status-cell">
                {actionState === RowState.RECOMMENDATION_ACCEPTED && <span className="status-badge recommendation">RECOMMENDATION ACCEPTED</span>}
                {actionState === RowState.OVERRIDE_PENDING && <span className="status-badge override">OVERRIDE PENDING</span>}
                {actionState === RowState.DELETE_PENDING && <span className="status-badge delete">DELETE PENDING</span>}
            </td>
        </tr>
    );
};
